import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { media } from '../theme/tokens';
import { PeriodToggle } from '../components/PeriodToggle';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/Button';
import { V2ContextBar } from '../components/V2ContextBar';
import { Card as V2Card, CardHeader as V2CardHeader } from '../components/Card';
import { ProgressBar } from '../components/ProgressBar';
import { Sparkline } from '../components/Sparkline';
import {
  KpiTile,
  ScrumItem,
  MarketRow,
  MixDataItem,
  BreakdownItem
} from './RoiDashboard.types';
import { useRoiDashboard } from 'apiTyped/hooks/v2.roi.query';
import { useV2Filters } from '../context/V2FiltersContext';

// ─── Component ───────────────────────────────────────────────────────────────

export const RoiDashboard: React.FC = () => {
  const { filters } = useV2Filters();
  const [period, setPeriod] = useState('6M');

  const {
    isLoading,
    breakdown,
    distributionKpis,
    engagementKpis,
    competitiveKpis,
    scrumItems,
    mixData,
    marketTable
  } = useRoiDashboard({
    market: filters.market,
    channel: filters.channel,
    brand: filters.brand,
    period: period.toLowerCase(),
    productType: filters.productType
  });

  if (isLoading && breakdown.length === 0) {
    return (
      <Page>
        <PageHeader
          title="ROI Dashboard"
          subtitle="Loading performance data…"
        />
        <V2ContextBar />
        <LoadingWrap>Loading…</LoadingWrap>
      </Page>
    );
  }

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="ROI Dashboard"
        subtitle="A synthesis of brand performance across distribution, engagement, and competitive position — updated daily from field surveys, scrapers, and email ingest."
      />

      {/* Context bar */}
      <V2ContextBar />

      {/* Period + export */}
      <Controls>
        <PeriodToggle
          options={[
            { label: '3M', value: '3M' },
            { label: '6M', value: '6M' },
            { label: '12M', value: '12M' }
          ]}
          value={period}
          onChange={setPeriod}
        />
        <Button
          icon={
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              width="14"
              height="14"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
          }
        >
          Export synthesis
        </Button>
      </Controls>

      {/* Hero — Brand Health Score */}
      <Hero>
        <HeroMain>
          <HeroLabel>Brand Health Score</HeroLabel>
          <HeroScore>
            74<HeroPts>/ 100</HeroPts>
          </HeroScore>
          <HeroTrend>
            <HeroDelta>↑ +6.2pt</HeroDelta>
            vs. previous 6 months
          </HeroTrend>
        </HeroMain>
        <HeroBreakdown>
          <HeroLabel style={{ marginBottom: 0 }}>Composite breakdown</HeroLabel>
          {breakdown.map((b) => (
            <ProgressBar
              key={b.label}
              percent={b.value}
              label={b.label}
              showValue
              height={8}
            />
          ))}
        </HeroBreakdown>
        <HeroSpark>
          <HeroSparkLabel>Score · last 6 months</HeroSparkLabel>
          <svg viewBox="0 0 280 90" style={{ width: '100%', height: 90 }}>
            <defs>
              <linearGradient id="heroGrad" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#c84b1f" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#c84b1f" stopOpacity={0} />
              </linearGradient>
            </defs>
            <path
              d="M0,70 L40,62 L80,68 L120,52 L160,48 L200,38 L240,30 L280,22 L280,90 L0,90 Z"
              fill="url(#heroGrad)"
            />
            <path
              d="M0,70 L40,62 L80,68 L120,52 L160,48 L200,38 L240,30 L280,22"
              stroke="#c84b1f"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <circle cx="280" cy="22" r="4" fill="#c84b1f" />
            <circle cx="280" cy="22" r="8" fill="#c84b1f" opacity={0.25} />
          </svg>
        </HeroSpark>
      </Hero>

      {/* Distribution KPIs */}
      <SectionHeading>
        Distribution <SectionCount>4 metrics</SectionCount>
      </SectionHeading>
      <KpiGrid>
        {distributionKpis.map((kpi) => (
          <KpiTileCard key={kpi.label} $featured={!!kpi.featured}>
            <KpiTileHead>
              <KpiTileLabel>{kpi.label}</KpiTileLabel>
            </KpiTileHead>
            <KpiTileValue>
              {kpi.value}
              {kpi.unit && <KpiUnit>{kpi.unit}</KpiUnit>}
            </KpiTileValue>
            <KpiTileFoot>
              <Delta $direction={kpi.direction}>{kpi.delta}</Delta>
              <Sparkline path={kpi.sparkPath} direction={kpi.direction} />
            </KpiTileFoot>
          </KpiTileCard>
        ))}
      </KpiGrid>

      {/* Engagement KPIs */}
      <SectionHeading>
        Engagement & Visibility <SectionCount>4 metrics</SectionCount>
      </SectionHeading>
      <KpiGrid>
        {engagementKpis.map((kpi) => (
          <KpiTileCard key={kpi.label}>
            <KpiTileHead>
              <KpiTileLabel>{kpi.label}</KpiTileLabel>
            </KpiTileHead>
            <KpiTileValue>
              {kpi.value}
              {kpi.unit && <KpiUnit>{kpi.unit}</KpiUnit>}
            </KpiTileValue>
            <KpiTileFoot>
              <Delta $direction={kpi.direction}>{kpi.delta}</Delta>
              <Sparkline path={kpi.sparkPath} direction={kpi.direction} />
            </KpiTileFoot>
          </KpiTileCard>
        ))}
      </KpiGrid>

      {/* Competitive KPIs */}
      <SectionHeading>
        Competitive position <SectionCount>4 metrics</SectionCount>
      </SectionHeading>
      <KpiGrid>
        {competitiveKpis.map((kpi) => (
          <KpiTileCard key={kpi.label}>
            <KpiTileHead>
              <KpiTileLabel>{kpi.label}</KpiTileLabel>
            </KpiTileHead>
            <KpiTileValue>
              {kpi.value}
              {kpi.unit && <KpiUnit>{kpi.unit}</KpiUnit>}
            </KpiTileValue>
            <KpiTileFoot>
              <Delta $direction={kpi.direction}>{kpi.delta}</Delta>
              <Sparkline path={kpi.sparkPath} direction={kpi.direction} />
            </KpiTileFoot>
          </KpiTileCard>
        ))}
      </KpiGrid>

      {/* Evolution Chart + Scrum Distri */}
      <Split>
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Brand Evolution — 6 months</CardTitle>
              <CardMeta>Distribution score · weekly cadence</CardMeta>
            </div>
            <ChartLegend>
              <LegendItem>
                <LegendSwatch $color="var(--terracotta)" />
                Your brand
              </LegendItem>
              <LegendItem>
                <LegendSwatch $color="var(--indigo)" />
                Top competitor
              </LegendItem>
              <LegendItem>
                <LegendSwatch $color="var(--ink-faint)" $dashed />
                Market avg.
              </LegendItem>
            </ChartLegend>
          </CardHeader>
          <EvolutionChart viewBox="0 0 640 220" preserveAspectRatio="none">
            {/* Grid lines */}
            <g stroke="var(--line-soft)" strokeWidth="1">
              <line x1="0" y1="40" x2="640" y2="40" />
              <line x1="0" y1="90" x2="640" y2="90" />
              <line x1="0" y1="140" x2="640" y2="140" />
              <line x1="0" y1="190" x2="640" y2="190" />
            </g>
            {/* Y labels */}
            <g
              fontFamily="var(--font-mono)"
              fontSize="9"
              fill="var(--ink-faint)"
            >
              <text x="6" y="44">
                100
              </text>
              <text x="6" y="94">
                75
              </text>
              <text x="6" y="144">
                50
              </text>
              <text x="6" y="194">
                25
              </text>
            </g>
            {/* X labels */}
            <g
              fontFamily="var(--font-mono)"
              fontSize="9"
              fill="var(--ink-faint)"
              textAnchor="middle"
            >
              <text x="80" y="212">
                Dec
              </text>
              <text x="180" y="212">
                Jan
              </text>
              <text x="280" y="212">
                Feb
              </text>
              <text x="380" y="212">
                Mar
              </text>
              <text x="480" y="212">
                Apr
              </text>
              <text x="580" y="212">
                May
              </text>
            </g>
            {/* Market avg dashed */}
            <path
              d="M40,120 L140,118 L240,115 L340,112 L440,110 L540,108 L620,108"
              stroke="var(--ink-faint)"
              strokeWidth="1.5"
              fill="none"
              strokeDasharray="4 4"
            />
            {/* Competitor */}
            <path
              d="M40,95 L140,92 L240,98 L340,90 L440,88 L540,86 L620,84"
              stroke="var(--indigo)"
              strokeWidth="2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* Your brand (with area) */}
            <defs>
              <linearGradient id="brandArea" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#c84b1f" stopOpacity={0.18} />
                <stop offset="100%" stopColor="#c84b1f" stopOpacity={0} />
              </linearGradient>
            </defs>
            <path
              d="M40,110 L140,102 L240,108 L340,82 L440,72 L540,62 L620,52 L620,200 L40,200 Z"
              fill="url(#brandArea)"
            />
            <path
              d="M40,110 L140,102 L240,108 L340,82 L440,72 L540,62 L620,52"
              stroke="var(--terracotta)"
              strokeWidth="2.5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {/* End dots */}
            <circle cx="620" cy="52" r="4" fill="var(--terracotta)" />
            <circle cx="620" cy="84" r="3.5" fill="var(--indigo)" />
          </EvolutionChart>
        </Card>

        <Card>
          <CardHeader>
            <div>
              <CardTitle>Scrum Distri</CardTitle>
              <CardMeta>8 items for Monday standup</CardMeta>
            </div>
          </CardHeader>
          <ScrumList>
            {scrumItems.map((item) => (
              <ScrumItemRow key={item.id} $priority={item.priority}>
                <ScrumIcon>
                  {item.priority === 'high' && (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
                      <path d="M12 9v4" />
                      <path d="M12 17h.01" />
                    </svg>
                  )}
                  {item.priority === 'med' && (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 8v4l3 2" />
                    </svg>
                  )}
                  {item.priority === 'pos' && (
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
                      <polyline points="16 7 22 7 22 13" />
                    </svg>
                  )}
                </ScrumIcon>
                <ScrumContent>
                  <ScrumTitle>{item.title}</ScrumTitle>
                  <ScrumDesc>{item.desc}</ScrumDesc>
                  <ScrumWhen>{item.when}</ScrumWhen>
                </ScrumContent>
              </ScrumItemRow>
            ))}
          </ScrumList>
        </Card>
      </Split>

      {/* Marketing Mix */}
      <Card style={{ marginBottom: 24 }}>
        <CardHeader>
          <div>
            <CardTitle>Marketing mix</CardTitle>
            <CardMeta>Listing-level visibility metrics across all POS</CardMeta>
          </div>
          <CardMeta>Across 247 active POS</CardMeta>
        </CardHeader>
        <MixGrid>
          {mixData.map((m) => (
            <MixTile key={m.label}>
              <MixLabel>{m.label}</MixLabel>
              <MixValue>
                {m.value}
                {m.unit && <MixUnit>{m.unit}</MixUnit>}
              </MixValue>
              <MixBarRow>
                <MixMiniBar>
                  <MixMiniBarFill style={{ width: `${m.barWidth}%` }} />
                </MixMiniBar>
                <span>{m.note}</span>
              </MixBarRow>
            </MixTile>
          ))}
        </MixGrid>
      </Card>

      {/* Market Detail Table */}
      <TableWrap>
        <TableHead>
          <div>
            <CardTitle>By market</CardTitle>
            <CardMeta>All KPIs, broken down by country</CardMeta>
          </div>
          <ViewAllBtn>View all 12 markets →</ViewAllBtn>
        </TableHead>
        <StyledTable>
          <thead>
            <tr>
              <th>Market</th>
              <NumTh>ND</NumTh>
              <NumTh>POS Won</NumTh>
              <NumTh>POS Lost</NumTh>
              <NumTh>Loyalty</NumTh>
              <NumTh>Novelty</NumTh>
              <NumTh>Mkt Share</NumTh>
              <NumTh>Score</NumTh>
            </tr>
          </thead>
          <tbody>
            {marketTable.map((row) => (
              <tr key={row.name}>
                <td>
                  <MarketCell>
                    <MarketFlag>{row.flag}</MarketFlag>
                    {row.name}
                  </MarketCell>
                </td>
                <NumTd>{row.nd}</NumTd>
                <NumTd $color="var(--olive)">{row.posWon}</NumTd>
                <NumTd $color="var(--crimson)">{row.posLost}</NumTd>
                <NumTd>{row.loyalty}</NumTd>
                <NumTd>{row.novelty}</NumTd>
                <NumTd>{row.mktShare}</NumTd>
                <NumTd $bold>{row.score}</NumTd>
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </TableWrap>
    </Page>
  );
};

// ─── Styled Components ───────────────────────────────────────────────────────

const Page = styled.div`
  padding: 24px 0;
`;

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  color: var(--ink-faint);
  font-size: 14px;
`;

const Controls = styled.section`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin: 20px 0 24px;
`;

// ─── Hero ────────────────────────────────────────────────────────────────────

const Hero = styled.section`
  background: linear-gradient(135deg, var(--ink) 0%, #2a2620 100%);
  color: var(--paper);
  border-radius: 14px;
  padding: 28px 32px;
  margin-bottom: 22px;
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  ${media.lg} {
    grid-template-columns: 1.2fr 1fr 1fr;
    gap: 32px;
    align-items: center;
  }

  &::before {
    content: '';
    position: absolute;
    top: -120px;
    right: -80px;
    width: 320px;
    height: 320px;
    background: radial-gradient(circle, var(--terracotta) 0%, transparent 70%);
    opacity: 0.25;
    pointer-events: none;
  }
`;

const HeroMain = styled.div``;

const HeroLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgba(248, 245, 240, 0.55);
  margin-bottom: 10px;
  font-weight: 500;
`;

const HeroScore = styled.div`
  font-family: var(--font-serif);
  font-size: 72px;
  font-weight: 400;
  letter-spacing: -0.04em;
  line-height: 0.95;
  display: flex;
  align-items: baseline;
  gap: 12px;
`;

const HeroPts = styled.span`
  font-size: 18px;
  color: rgba(248, 245, 240, 0.55);
  font-family: var(--font-sans);
  letter-spacing: 0;
`;

const HeroTrend = styled.div`
  margin-top: 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--terracotta-soft);
  font-size: 13px;
  font-weight: 500;
`;

const HeroDelta = styled.span`
  background: rgba(200, 75, 31, 0.2);
  padding: 3px 8px;
  border-radius: 4px;
  color: var(--terracotta-soft);
  font-family: var(--font-mono);
`;

const HeroBreakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const HeroSpark = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const HeroSparkLabel = styled.div`
  font-size: 11px;
  color: rgba(248, 245, 240, 0.55);
  text-transform: uppercase;
  letter-spacing: 0.12em;
  margin-bottom: 12px;
`;

// ─── KPI Grid ────────────────────────────────────────────────────────────────

const SectionHeading = styled.h2`
  font-family: var(--font-serif);
  font-size: 18px;
  font-weight: 500;
  margin: 0 0 14px;
  display: flex;
  align-items: baseline;
  gap: 10px;
`;

const SectionCount = styled.span`
  font-family: var(--font-sans);
  font-size: 12px;
  color: var(--ink-faint);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 500;
`;

const KpiGrid = styled.section`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 24px;

  ${media.lg} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const KpiTileCard = styled.div<{ $featured?: boolean }>`
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 16px 18px;
  transition: all 0.15s;
  cursor: pointer;

  &:hover {
    border-color: var(--ink-faint);
    box-shadow: var(--shadow-md);
  }

  ${({ $featured }) =>
    $featured &&
    css`
      border-color: var(--terracotta);
      background: linear-gradient(180deg, var(--white) 0%, #fef9f5 100%);
    `}
`;

const KpiTileHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 8px;
`;

const KpiTileLabel = styled.span`
  font-size: 11.5px;
  color: var(--ink-faint);
  font-weight: 500;
  line-height: 1.3;
`;

const KpiTileValue = styled.div`
  font-family: var(--font-serif);
  font-size: 28px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1;
  margin-bottom: 8px;
`;

const KpiUnit = styled.span`
  font-size: 14px;
  color: var(--ink-faint);
  margin-left: 2px;
  font-family: var(--font-sans);
`;

const KpiTileFoot = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
`;

const Delta = styled.span<{ $direction: 'up' | 'down' | 'flat' }>`
  font-family: var(--font-mono);
  font-size: 11.5px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: ${({ $direction }) =>
    $direction === 'up'
      ? 'var(--olive)'
      : $direction === 'down'
        ? 'var(--crimson)'
        : 'var(--ink-faint)'};
`;

// ─── Split / Cards ───────────────────────────────────────────────────────────

const Split = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 24px;

  ${media.lg} {
    grid-template-columns: 2fr 1fr;
  }
`;

const Card = styled.div`
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 20px 22px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
`;

const CardTitle = styled.div`
  font-family: var(--font-serif);
  font-size: 15px;
  font-weight: 600;
`;

const CardMeta = styled.div`
  font-size: 12px;
  color: var(--ink-faint);
  margin-top: 2px;
`;

const ChartLegend = styled.div`
  display: flex;
  gap: 14px;
  font-size: 12px;
  color: var(--ink-muted);
  align-items: center;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

const LegendSwatch = styled.span<{ $color: string; $dashed?: boolean }>`
  display: inline-block;
  width: 12px;
  height: ${({ $dashed }) => ($dashed ? '1px' : '2px')};
  background: ${({ $color }) => $color};
  ${({ $dashed }) =>
    $dashed &&
    css`
      border-top: 1px dashed;
      background: none;
    `}
`;

const EvolutionChart = styled.svg`
  width: 100%;
  height: 220px;
`;

// ─── Scrum ───────────────────────────────────────────────────────────────────

const ScrumList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ScrumItemRow = styled.div<{ $priority: 'high' | 'med' | 'pos' }>`
  display: flex;
  gap: 10px;
  padding: 10px;
  border-radius: 7px;
  background: var(--paper);
  border-left: 3px solid
    ${({ $priority }) =>
      $priority === 'high'
        ? 'var(--crimson)'
        : $priority === 'med'
          ? 'var(--amber)'
          : 'var(--olive)'};
  font-size: 13px;
  transition: all 0.15s;
  cursor: pointer;

  &:hover {
    background: var(--paper-warm);
  }
`;

const ScrumIcon = styled.div`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: var(--white);
  display: grid;
  place-items: center;
  color: var(--ink-muted);

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ScrumContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ScrumTitle = styled.div`
  font-weight: 600;
  color: var(--ink);
  margin-bottom: 2px;
  font-size: 13px;
`;

const ScrumDesc = styled.div`
  font-size: 12px;
  color: var(--ink-muted);
  line-height: 1.4;
`;

const ScrumWhen = styled.div`
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--ink-faint);
  margin-top: 4px;
`;

// ─── Marketing Mix ───────────────────────────────────────────────────────────

const MixGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;

  ${media.lg} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const MixTile = styled.div`
  background: var(--paper);
  border-radius: 8px;
  padding: 14px 16px;
`;

const MixLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--ink-faint);
  margin-bottom: 8px;
  font-weight: 500;
`;

const MixValue = styled.div`
  font-family: var(--font-serif);
  font-size: 24px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1;
  margin-bottom: 6px;
`;

const MixUnit = styled.span`
  font-size: 12px;
  color: var(--ink-faint);
  font-family: var(--font-sans);
  margin-left: 2px;
`;

const MixBarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  font-size: 11px;
  color: var(--ink-faint);
  margin-top: 4px;
`;

const MixMiniBar = styled.span`
  flex: 1;
  height: 3px;
  background: var(--line-soft);
  border-radius: 1.5px;
  overflow: hidden;
`;

const MixMiniBarFill = styled.div`
  height: 100%;
  background: var(--terracotta);
  border-radius: 1.5px;
`;

// ─── Market Table ────────────────────────────────────────────────────────────

const TableWrap = styled.div`
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: 10px;
  overflow: hidden;
`;

const TableHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 22px;
  border-bottom: 1px solid var(--line);
`;

const ViewAllBtn = styled.button`
  background: none;
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  color: var(--ink-muted);
  cursor: pointer;
  font-family: var(--font-sans);
  transition: all 0.15s;

  &:hover {
    border-color: var(--ink-faint);
    color: var(--ink);
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead th {
    background: var(--paper);
    text-align: left;
    padding: 10px 14px;
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--ink-faint);
    font-weight: 600;
    border-bottom: 1px solid var(--line);
  }

  tbody tr {
    border-bottom: 1px solid var(--line-soft);
    transition: background 0.1s;

    &:hover {
      background: var(--paper);
    }

    &:last-child {
      border-bottom: none;
    }
  }

  tbody td {
    padding: 12px 14px;
    font-size: 13px;
  }
`;

const NumTh = styled.th`
  && {
    text-align: right;
  }
`;

const NumTd = styled.td<{ $color?: string; $bold?: boolean }>`
  && {
    text-align: right;
    font-family: var(--font-mono);
    font-size: 12.5px;
    ${({ $color }) =>
      $color &&
      css`
        color: ${$color};
      `}
    ${({ $bold }) =>
      $bold &&
      css`
        font-weight: 600;
      `}
  }
`;

const MarketCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
`;

const MarketFlag = styled.span`
  font-size: 16px;
`;
